import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import TiptapEditorLazy from './TiptapEditor.lazy';
import {
  DEFAULT_CHECKIN_RULES_HTML,
  plainTextLengthFromHtml,
} from './checkinRulesDefaults';

const RULES_MAX_CHARS = 2000;

function SystemSettings({ settings, onChange, onSave, saving, hasUnsavedChanges = true }) {
  const [enableConfirmTarget, setEnableConfirmTarget] = useState(null);

  const update = (field, value) => {
    onChange({ ...settings, [field]: value });
  };

  const closeEnableConfirm = () => setEnableConfirmTarget(null);

  const confirmEnableToggle = () => {
    if (enableConfirmTarget === null) return;
    update('enableCheckIn', enableConfirmTarget);
    setEnableConfirmTarget(null);
  };

  const handleEnableCheckboxChange = () => {
    setEnableConfirmTarget(!settings.enableCheckIn);
  };

  const rulesHtml =
    settings.rulesContent && String(settings.rulesContent).trim().length > 0
      ? settings.rulesContent
      : DEFAULT_CHECKIN_RULES_HTML;

  const charCount = plainTextLengthFromHtml(rulesHtml);
  const overLimit = charCount > RULES_MAX_CHARS;

  const handleRulesEditorUpdate = (html) => {
    onChange({ ...settings, rulesContent: html });
  };

  return (
    <div>
      <div style={styles.section}>
        <div style={styles.toggleGroup}>
          <div style={styles.toggleRow}>
            <div style={styles.toggleInfo}>
              <span style={styles.toggleLabel}>Enable Check-in</span>
              <span style={styles.toggleDesc}>
                Allow users to perform daily check-ins
              </span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.enableCheckIn || false}
                onChange={handleEnableCheckboxChange}
              />
              <span className="toggle-slider" />
            </label>
          </div>

          
        </div>
      </div>

      <p className="form-hint" style={{ marginBottom: 20 }}>
        Check-in days use midnight Asia/Shanghai as the calendar boundary.
      </p>

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Rules & Content</h3>
        <div className="form-group">
          <TiptapEditorLazy
            content={rulesHtml}
            onUpdate={handleRulesEditorUpdate}
            maxChars={RULES_MAX_CHARS}
            charCount={charCount}
            label="Check-in Rules"
            labelClassName="tiptap-editor-label--checkin-rules"
          />
        </div>
      </div>

      <div style={styles.footer}>
        <button
          className="btn btn-primary"
          disabled={saving || overLimit || !hasUnsavedChanges}
          onClick={onSave}
        >
          {saving ? 'Saving...' : 'Save System Settings'}
        </button>
      </div>

      {enableConfirmTarget !== null &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="modal-overlay system-enable-confirm-overlay"
            role="presentation"
            onClick={closeEnableConfirm}
          >
            <div
              className="modal-content system-enable-confirm-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="system-enable-checkin-confirm-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2 id="system-enable-checkin-confirm-title">
                  {enableConfirmTarget ? 'Enable check-in?' : 'Disable check-in?'}
                </h2>
                <button
                  type="button"
                  className="modal-close"
                  onClick={closeEnableConfirm}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                {enableConfirmTarget ? (
                  <p style={modalBodyTextStyle}>
                    If you continue and <strong>save system settings</strong>, daily check-in is turned
                    <strong> on</strong> for your tenant. Users will be able to open and use check-in again,
                    subject to your reward configuration.
                  </p>
                ) : (
                  <p style={modalBodyTextStyle}>
                    If you continue and <strong>save system settings</strong>, daily check-in is turned{' '}
                    <strong>off</strong> for your tenant <strong>immediately</strong> after that save. Users will{' '}
                    <strong>no longer be able to access</strong> the check-in experience until you enable it
                    again and save.
                  </p>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeEnableConfirm}>
                  Cancel
                </button>
                <button
                  type="button"
                  className={enableConfirmTarget ? 'btn btn-primary' : 'btn btn-danger'}
                  onClick={confirmEnableToggle}
                >
                  {enableConfirmTarget ? 'Enable' : 'Disable'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

const modalBodyTextStyle = {
  margin: 0,
  fontSize: '0.95rem',
  lineHeight: 1.55,
  color: '#374151',
};

const styles = {
  section: {
    marginBottom: 28,
  },
  sectionTitle: {
    fontSize: '0.95rem',
    fontWeight: 700,
    color: '#1e1b4b',
    margin: '0 0 14px 0',
  },
  toggleGroup: {
    background: '#f9fafb',
    borderRadius: 12,
    border: '1px solid #e5e7eb',
    overflow: 'hidden',
  },
  toggleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '18px 20px',
  },
  toggleInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  toggleLabel: {
    fontWeight: 600,
    fontSize: '0.9rem',
    color: '#1e1b4b',
  },
  toggleDesc: {
    fontSize: '0.8rem',
    color: '#6b7280',
  },
  divider: {
    height: 1,
    background: '#e5e7eb',
    margin: '0 20px',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingTop: 8,
  },
};

export default SystemSettings;
